import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaCode, 
  FaMobile, 
  FaServer, 
  FaCloud, 
  FaPalette, 
  FaRocket,
  FaShieldAlt,
  FaCog,
  FaChartLine
} from 'react-icons/fa';
import './Services.css';

const Services = () => {
  const services = [
    {
      icon: <FaCode />,
      title: "Web Development",
      description: "Custom web applications built with modern frameworks like React.js and Next.js and Shopify. Responsive, fast, and SEO-optimized.",
      features: ["React.js", "Next.js", "Shopify", "WordPress", "HTML", "CSS", "JavaScript"],
      color: "#6366f1"
    },
    {
      icon: <FaMobile />,
      title: "Mobile Development",
      description: "Native and cross-platform mobile apps for iOS and Android. From concept to App Store deployment.",
      features: ["React Native", "Flutter", "iOS", "Android"],
      color: "#ec4899"
    },
    {
      icon: <FaServer />,
      title: "Backend Development",
      description: "Scalable server-side solutions with robust APIs, databases, and microservices architecture.",
      features: ["Node.js", "PHP", "MySQL", "PostgreSQL"],
      color: "#8b5cf6"
    },
    {
      icon: <FaPalette />,
      title: "UI/UX Design",
      description: "Beautiful, intuitive user interfaces that enhance user experience and drive engagement.",
      features: ["Figma", "Adobe XD", "Photoshop", "User Research"],
      color: "#f59e0b"
    },
    {
      icon: <FaCloud />,
      title: "Cloud Solutions",
      description: "Cloud infrastructure setup, migration, and optimization for better performance and scalability.",
      features: ["AWS", "Azure", "Google Cloud", "Docker"],
      color: "#10b981"
    },
    {
      icon: <FaRocket />,
      title: "DevOps & Deployment",
      description: "Automated deployment pipelines, CI/CD, and infrastructure management for seamless delivery.",
      features: ["CI/CD", "Kubernetes", "Monitoring", "Security"],
      color: "#ef4444"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="services" className="services">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="section-badge">Our Services</span>
          <h2 className="section-title">
            Comprehensive <span className="gradient-text">Codexa Works</span>
          </h2>
          <p className="section-description">
            We provide end-to-end technology services to help your business thrive in the digital age. 
            From concept to deployment, we've got you covered.
          </p>
        </motion.div>

        <motion.div
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card"
              variants={itemVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
            >
              <div 
                className="service-icon"
                style={{ color: service.color }}
              >
                {service.icon}
              </div>
              
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
              
              <div className="service-features">
                {service.features.map((feature, featureIndex) => (
                  <span key={featureIndex} className="feature-tag">
                    {feature}
                  </span>
                ))}
              </div>

              <motion.button
                className="service-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ 
                  background: `linear-gradient(135deg, ${service.color}, ${service.color}dd)`,
                  borderColor: service.color
                }}
              >
                Learn More
              </motion.button>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="services-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <h3>Ready to Transform Your Business?</h3>
            <p>Let's discuss your project and create something amazing together.</p>
            <motion.button
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Get Started Today
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;

